import React, { useState } from 'react';

// Shared UI Primitives

const Icon = ({ path, className = "w-5 h-5" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
        <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
);

const Section = ({ children, className = "" }) => (
    <section className={`py-14 md:py-20 px-4 ${className}`}>
        <div className="container mx-auto max-w-5xl">{children}</div>
    </section>
);

const Card = ({ children, className = "" }) => (
    <div className={`bg-black/20 backdrop-blur-2xl rounded-2xl p-6 md:p-8 border border-white/10 shadow-lg ${className}`}>
        {children}
    </div>
);

const Tag = ({ children, color = "emerald" }) => {
    const colors = {
        emerald: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
        indigo:  "bg-indigo-500/10 text-indigo-300 border-indigo-500/20",
        amber:   "bg-amber-500/10 text-amber-300 border-amber-500/20",
        sky:     "bg-sky-500/10 text-sky-300 border-sky-500/20",
        purple:  "bg-purple-500/10 text-purple-300 border-purple-500/20",
        rose:    "bg-rose-500/10 text-rose-300 border-rose-500/20",
    };
    return (
        <span className={`text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full border ${colors[color]}`}>
            {children}
        </span>
    );
};

// Header

const Header = () => (
    <header className="fixed top-0 left-0 right-0 z-50 p-4">
        <div className="container mx-auto max-w-5xl">
            <div className="px-6 py-3 rounded-full flex items-center justify-between"
                style={{
                    background: 'rgba(0,0,0,0.3)',
                    backdropFilter: 'blur(20px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.37)'
                }}>
                <div className="text-white font-bold text-sm tracking-wider">SOCIAL SEO</div>
                <div className="text-gray-400 text-xs tracking-wider uppercase">Kush / The United Page</div>
            </div>
        </div>
    </header>
);

// Hero

const Hero = () => (
    <Section className="pt-36 md:pt-44 text-center">
        <div className="inline-flex mb-6">
            <Tag color="rose">Production Plan / Locked With Kush</Tag>
        </div>
        <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-tight tracking-tight">
            The United Page,<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-400 via-red-300 to-amber-400">
                Built Around Your Face
            </span>
        </h1>
        <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-12 leading-relaxed">
            A Manchester United fan page with a face. Your opinions, United news and banter, produced in the mold of The United Stand and Full Time Devils. Exactly the page you described in the questionnaire, ready for the season.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {[
                { label: "United Content", val: "90%" },
                { label: "Football Business", val: "10%" },
                { label: "Output vs Today", val: "3x" },
                { label: "Kickoff", val: "Jul 15" },
            ].map((item, i) => (
                <div key={i} className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 text-center">
                    <div className="text-2xl font-extrabold text-white mb-1">{item.val}</div>
                    <div className="text-gray-400 text-xs uppercase tracking-wider">{item.label}</div>
                </div>
            ))}
        </div>
    </Section>
);

// The Big Call

const BigCall = () => (
    <Section>
        <div className="text-center mb-12">
            <Tag color="rose">The Big Call / Your Words</Tag>
            <h2 className="text-4xl font-extrabold text-white mt-4 mb-4">A fan page with a face</h2>
            <p className="text-gray-400 max-w-2xl mx-auto leading-relaxed">
                Not a meme-only aggregator, and not a business channel. A United page where you give opinions, react to news, and let the memes do the reach work. Goldbridge, Full Time Devils and GoodVibesJohn all rode this exact model into serious monetisation. That is the lane.
            </p>
        </div>

        <Card className="bg-gradient-to-r from-rose-900/20 via-red-900/10 to-amber-900/20 border-white/10 text-center mb-8">
            <p className="text-white font-medium text-lg leading-relaxed">
                90% Manchester United content. 10% business of football. English only. Zero India focus. Strong opinions, measured analysis, and full licence to criticise United when they deserve it. Your AI page stays untouched on its own handle, fully protected.
            </p>
        </Card>

        <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-l-4 border-rose-500/60">
                <h3 className="text-lg font-bold text-white mb-2">The United Stand</h3>
                <p className="text-sm text-gray-300 leading-relaxed">
                    The opinion engine. Owned takes, player ratings, matchday emotion. We borrow the confidence, skip the rant.
                </p>
            </Card>
            <Card className="border-l-4 border-amber-500/60">
                <h3 className="text-lg font-bold text-white mb-2">Full Time Devils</h3>
                <p className="text-sm text-gray-300 leading-relaxed">
                    The fan-TV energy. Fast cuts between your face and the football, captions burned in, zero dead air.
                </p>
            </Card>
            <Card className="border-l-4 border-sky-500/60">
                <h3 className="text-lg font-bold text-white mb-2">GoodVibesJohn</h3>
                <p className="text-sm text-gray-300 leading-relaxed">
                    The warmth. 117K followers on barely 2,000 posts, because people follow the person. Proof the face beats the feed.
                </p>
            </Card>
        </div>
    </Section>
);

// Competitor Map

const competitors = [
    { handle: "weareunited", followers: "3M", note: "Category ceiling. Anonymous culture page." },
    { handle: "fulltimedevils", followers: "957K", note: "Fan TV with faces. Built a 780K-sub YouTube business." },
    { handle: "theunitedstandtv", followers: "471K", note: "Goldbridge. 2.26M on YouTube off the same fan base." },
    { handle: "utdinside", followers: "429K", note: "News community. No face, no brand deals." },
    { handle: "mufcscoop", followers: "346K", note: "Scoop reposts at volume. 12K posts." },
    { handle: "mufc__latest", followers: "269K", note: "Rapid news drops. 15K posts to get there." },
    { handle: "mufcfamly", followers: "167K", note: "Memes and community. 22K posts." },
    { handle: "centredevils", followers: "117K", note: "All-round coverage, mid-pack." },
    { handle: "goodvibesjohn", followers: "117K", note: "Face-led. Same size on 10x fewer posts." },
    { handle: "mufcunion", followers: "102K", note: "The baseline for generic posting." },
];

const CompetitorMap = () => (
    <Section>
        <div className="text-center mb-12">
            <Tag color="sky">The Map / Your 10 References</Tag>
            <h2 className="text-4xl font-extrabold text-white mt-4 mb-4">Faces build businesses. Feeds stay anonymous.</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
                Every page you sent, sized up. The pattern is loud: the anonymous pages grind out 10,000+ posts for reach, while the pages with a face turn the same audience into YouTube channels, sponsorships and real money. We build the second kind, and use the first kind's formats for reach.
            </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
            {competitors.map((c, i) => (
                <div key={i} className="bg-black/20 rounded-xl p-4 border border-white/10 flex items-center gap-4">
                    <div className="text-xl font-extrabold text-transparent bg-clip-text bg-gradient-to-br from-rose-300 to-amber-400 w-16 flex-shrink-0 text-right">{c.followers}</div>
                    <div>
                        <div className="font-mono text-sm font-bold text-white">@{c.handle}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{c.note}</div>
                    </div>
                </div>
            ))}
        </div>
        <p className="text-center text-gray-500 text-xs mt-6">Follower counts approximate, July 2026.</p>
    </Section>
);

// Content Engine

const formats = [
    {
        share: "50%",
        title: "Face Reels",
        accent: "border-rose-500",
        desc: "Hot-take talking heads. Your opinion on the story of the day, captions burned in, one graphic behind you. This is the brand. Low tension music bed under speech.",
    },
    {
        share: "30%",
        title: "News & Number Drops",
        accent: "border-amber-500",
        desc: "Transfer fees, stats, confirmed deals. Bold text on screen, riser into a beat drop on the reveal. Every fact verified from two sources before it goes up. Scoop-page reach, without the fake news.",
    },
    {
        share: "20%",
        title: "Banter & Edits",
        accent: "border-sky-500",
        desc: "Memes, rival chaos, nostalgia and legend edits on trending audio or phonk. The share engine that keeps the page fun between takes.",
    },
];

const ContentEngine = () => (
    <Section>
        <div className="text-center mb-12">
            <Tag color="rose">The Content Engine</Tag>
            <h2 className="text-4xl font-extrabold text-white mt-4 mb-4">Three formats, one voice</h2>
            <p className="text-gray-400 max-w-lg mx-auto">
                3x the output of your current plan, in the same budget. Face reels build the brand, news drops build the reach, banter builds the community.
            </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
            {formats.map((f, i) => (
                <Card key={i} className={`border-l-4 ${f.accent}`}>
                    <div className="text-3xl font-extrabold text-white mb-1">{f.share}</div>
                    <h3 className="text-lg font-bold text-white mb-3">{f.title}</h3>
                    <p className="text-sm text-gray-300 leading-relaxed">{f.desc}</p>
                </Card>
            ))}
        </div>
    </Section>
);

// Demo Script

const DemoScript = () => (
    <Section>
        <div className="text-center mb-12">
            <Tag color="amber">Proof / Reel 01 Is Already Written</Tag>
            <h2 className="text-4xl font-extrabold text-white mt-4 mb-4">Your first hot take, scripted</h2>
            <p className="text-gray-400 max-w-xl mx-auto">
                Built from your own words about this window, with every fee verified before you record. This is the voice of the page.
            </p>
        </div>

        <Card className="bg-gradient-to-br from-rose-900/25 via-black/20 to-amber-900/15 border-white/10">
            <div className="text-center mb-8">
                <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-3">Text hook on screen</div>
                <div className="text-2xl md:text-4xl font-extrabold text-white tracking-tight">SPURS JUST ROBBED<br />UNITED'S MIDFIELD</div>
            </div>
            <div className="space-y-4 max-w-2xl mx-auto">
                {[
                    { who: "Spoken hook", line: "Tottenham just spent 185 million pounds building the midfield Manchester United were supposed to build." },
                    { who: "The numbers", line: "Fernandes to Spurs for a reported 85 million. Tonali for a reported 100. Our answer: Santos and Ederson for a combined 88." },
                    { who: "The take", line: "Fernandes and Tonali were certainty. Santos and Ederson are a bet. Spurs bought answers. We bought homework." },
                    { who: "The CTA", line: "Am I wrong? Tell me why Santos and Ederson are enough. And follow, because this window is not done yet." },
                ].map((r, i) => (
                    <div key={i} className="flex gap-4 items-start">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-amber-300 bg-amber-500/10 border border-amber-500/20 px-2.5 py-1 rounded-full flex-shrink-0 mt-1 w-28 text-center">{r.who}</span>
                        <p className="text-gray-200 leading-relaxed">{r.line}</p>
                    </div>
                ))}
            </div>
        </Card>
    </Section>
);

// The Sprint

const sprint = [
    { when: "Now", move: "Lock the handle, design the identity, source-verify the first scripts, set the footage pipeline. Invoice settles and production starts." },
    { when: "Jul 15", move: "Page goes live into pre-season. Daily cadence from day one: face takes on tour games, signings, squad news." },
    { when: "Aug", move: "The season starts. Matchday formats switch on: pre-match takes, full-time reactions, player ratings. The best growth weeks of the year." },
    { when: "Sep", move: "Deadline-day peak. Transfer chaos is the biggest content event in football, and the page is built for it. Then we review the numbers and open brand-deal conversations." },
    { when: "Oct+", move: "Season rhythm. The face compounds, the audience knows you, and the business-of-football layer starts appearing through stories, exactly as you called it." },
];

const Sprint = () => (
    <Section>
        <div className="text-center mb-12">
            <Tag color="purple">The Sprint / Jul 15 to Sep 30</Tag>
            <h2 className="text-4xl font-extrabold text-white mt-4 mb-4">Launch into the loudest window of the year</h2>
            <p className="text-gray-400 max-w-2xl mx-auto leading-relaxed">
                Your call, and the right one: pre-season, the season opener and the end of the transfer window all land inside 11 weeks. We push hard through all of it.
            </p>
        </div>

        <div className="space-y-4">
            {sprint.map((s, i) => (
                <Card key={i} className="flex gap-5 items-start">
                    <div className="flex-shrink-0 w-16 text-center">
                        <div className="text-lg font-extrabold text-transparent bg-clip-text bg-gradient-to-br from-rose-300 to-amber-400">{s.when}</div>
                    </div>
                    <p className="text-gray-300 text-sm md:text-base leading-relaxed pt-0.5">{s.move}</p>
                </Card>
            ))}
        </div>
    </Section>
);

// Handle Picker

const handles = [
    { tag: "kushutd", vibe: "Clean and shortest. Your name plus the club." },
    { tag: "utdwithkush", vibe: "Watch United with Kush. The face is in the name." },
    { tag: "kushtalksunited", vibe: "Says exactly what the page is. Opinion-first." },
    { tag: "kushreddevil", vibe: "Pure fan identity." },
    { tag: "thedevilsdesk", vibe: "Editorial brand that can outgrow the handle later." },
];

const HandlePicker = () => {
    const [picked, setPicked] = useState(null);
    return (
        <Section>
            <div className="text-center mb-12">
                <Tag color="amber">Decision 1</Tag>
                <h2 className="text-4xl font-extrabold text-white mt-4 mb-4">Pick your handle</h2>
                <p className="text-gray-400 max-w-lg mx-auto">
                    Fresh shortlist for the fan page. Tap the one you like, or send your own. We check availability and lock it.
                </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                {handles.map((h, i) => {
                    const active = picked === i;
                    return (
                        <button
                            key={i}
                            onClick={() => setPicked(i)}
                            className={`text-left rounded-2xl p-5 border transition-all duration-200 ${
                                active
                                    ? 'bg-rose-500/15 border-rose-400/60 shadow-lg shadow-rose-900/30 scale-[1.01]'
                                    : 'bg-black/20 border-white/10 hover:border-white/25 hover:bg-black/30'
                            }`}
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className={`font-mono text-lg font-bold ${active ? 'text-rose-300' : 'text-white'}`}>@{h.tag}</div>
                                    <div className="text-sm text-gray-400 mt-1">{h.vibe}</div>
                                </div>
                                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${active ? 'border-rose-400 bg-rose-400/20' : 'border-white/20'}`}>
                                    {active && <Icon path="M4.5 12.75l6 6 9-13.5" className="w-3.5 h-3.5 text-rose-300" />}
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>
            {picked !== null && (
                <p className="text-center text-rose-300 text-sm mt-6">
                    Leaning toward <span className="font-mono font-bold">@{handles[picked].tag}</span>. Send it back and we lock it.
                </p>
            )}
        </Section>
    );
};

// The Money

const Money = () => (
    <Section>
        <div className="text-center mb-12">
            <Tag color="emerald">The Money</Tag>
            <h2 className="text-4xl font-extrabold text-white mt-4 mb-4">Brand deals, as soon as the numbers allow</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
                You asked for partnerships ASAP. The model your references prove: the face earns them. Goldbridge turned a fan channel into a 2.26M subscriber media business. Full Time Devils did the same. The anonymous pages earned nothing.
            </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-l-4 border-emerald-500/60">
                <h3 className="text-lg font-bold text-white mb-2">First: reach</h3>
                <p className="text-sm text-gray-300 leading-relaxed">
                    The Jul-Sep sprint is about audience. News drops and banter drive discovery, your face converts viewers into followers.
                </p>
            </Card>
            <Card className="border-l-4 border-emerald-500/60">
                <h3 className="text-lg font-bold text-white mb-2">Then: partners</h3>
                <p className="text-sm text-gray-300 leading-relaxed">
                    Sports brands, fantasy and gaming apps, streaming, kit and merch, football tech. Outreach starts once the page has proof, from October.
                </p>
            </Card>
            <Card className="border-l-4 border-emerald-500/60">
                <h3 className="text-lg font-bold text-white mb-2">Later: your story</h3>
                <p className="text-sm text-gray-300 leading-relaxed">
                    The football and business blend enters through stories once the brand stands on its own. Your call on the timing, and it stays that way.
                </p>
            </Card>
        </div>
    </Section>
);

// CTA

const CTA = () => (
    <Section className="pb-32">
        <Card className="bg-gradient-to-br from-rose-900/30 via-red-900/15 to-amber-900/30 border-white/10 text-center py-12">
            <Tag color="rose">What I Need From You</Tag>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mt-5 mb-6">Three things and we kick off.</h2>
            <div className="grid md:grid-cols-3 gap-4 max-w-3xl mx-auto mb-10 text-left">
                {[
                    "Pick a handle above, or send your own.",
                    "Green-light the demo script. That approves the voice for the whole launch batch.",
                    "Book the first recording slot this week. Payment link lands on WhatsApp alongside this page.",
                ].map((t, i) => (
                    <div key={i} className="bg-black/20 rounded-xl p-5 border border-white/10">
                        <div className="text-rose-300 font-extrabold text-lg mb-2">{i + 1}</div>
                        <p className="text-sm text-gray-300 leading-relaxed">{t}</p>
                    </div>
                ))}
            </div>
            <p className="text-gray-500 text-sm mt-2">
                The moment you send these back, we lock the handle, design the identity, and script week one. Live July 15.
            </p>
        </Card>
    </Section>
);

// Main Export

export default function KushFootballProposal() {
    return (
        <div className="font-sans text-slate-200">
            <div className="relative z-10">
                <Header />
                <main>
                    <Hero />
                    <BigCall />
                    <CompetitorMap />
                    <ContentEngine />
                    <DemoScript />
                    <Sprint />
                    <HandlePicker />
                    <Money />
                    <CTA />
                </main>
                <footer className="py-12 text-center text-gray-600 text-xs">
                    2026 Social SEO. Confidential Strategy Document.
                </footer>
            </div>
        </div>
    );
}
